import React from 'react';
import {
  Box,
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PatientDrug } from '../invoiceHelper';
import { formatDateDMY } from '../../../../utils/dateUtils';
import { CHECKED_ICON, UNCHECKED_ICON } from '../common/InvoiceTableCommon';

interface PatientDrugsTableProps {
  drugs: PatientDrug[];
  isLoading: boolean;
  selectedIndices: Set<number>;
  onDrugToggle: (index: number) => void;
  onSelectAllDrugs: (drugs: PatientDrug[]) => void;
}

const PatientDrugsTable: React.FC<PatientDrugsTableProps> = ({
  drugs,
  isLoading,
  selectedIndices,
  onDrugToggle,
  onSelectAllDrugs,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading prescriptions...</Typography>
      </Box>
    );
  }

  if (drugs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography color="text.secondary">No prescriptions found for this patient.</Typography>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        p: 2,
        boxShadow: 'none',
        border: '1px solid #E4E7EC',
        backgroundColor: '#FFFFFF',
        mb: 2,
      }}
    >
      <TableContainer sx={{ maxHeight: { xs: 500, md: 'calc(100vh - 320px)' }, overflowX: 'auto' }}>
        <Table sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
          <TableHead>
            <TableRow
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: '#F5F6F7',
                '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' },
                whiteSpace: 'nowrap',
              }}
            >
              <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                <Checkbox
                  checked={drugs.length > 0 && selectedIndices.size === drugs.length}
                  indeterminate={selectedIndices.size > 0 && selectedIndices.size < drugs.length}
                  onChange={() => onSelectAllDrugs(drugs)}
                  icon={UNCHECKED_ICON}
                  checkedIcon={CHECKED_ICON}
                />
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Drug Name</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Strength</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Instructions</TableCell>
              <TableCell>Prescribed By</TableCell>
              <TableCell sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drugs.map((drug, idx) => (
              <TableRow
                key={drug.id}
                onClick={() => onDrugToggle(idx)}
                sx={{
                  background: '#FFFFFF',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#F9FAFB' },
                  '& td': {
                    borderTop: idx === 0 ? 'none' : undefined,
                    borderBottom: idx === drugs.length - 1 ? 'none' : undefined,
                    paddingY: 1.25,
                  },
                }}
              >
                <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                  <Checkbox
                    checked={selectedIndices.has(idx)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onDrugToggle(idx)}
                    icon={UNCHECKED_ICON}
                    checkedIcon={CHECKED_ICON}
                    disableRipple
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {drug.created_on ? formatDateDMY(drug.created_on) : '-'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {drug.drug?.name_label || drug.drug?.name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>{drug.drug?.brand_name || '-'}</TableCell>
                <TableCell>{drug.drug?.strength || '-'}</TableCell>
                <TableCell>{drug.frequency || '-'}</TableCell>
                <TableCell>{drug.drug_route_code_label || drug.drug_route_code || '-'}</TableCell>
                <TableCell>{drug.no_of_days ?? '-'}</TableCell>
                <TableCell>{drug.quantity ?? '-'}</TableCell>
                <TableCell>{drug.instructions_label || drug.instructions || '-'}</TableCell>
                <TableCell>{drug.user_full_name || '-'}</TableCell>
                <TableCell>{drug.notes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default PatientDrugsTable;
